import React from 'react'
import { FormAutosuggest } from '@fs/zion-form'
import { Button, List, ListItem, Row, Separator, colors } from '@fs/zion-ui'
import { ControlSplit, MenuNewWindow, NoticeWarning } from '@fs/zion-icon'
import { getNestedOrFloatingElementById, getNestedSubelementsByType, useSlsStateContext } from '@fs/zion-sls'
import { useTranslation } from 'react-i18next'
import { useR2DispatchContext, useR2StateContext } from '../../../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import FieldWrapper from '../../../ui/FieldWrapper'

const RelationshipField = ({
  relationshipField,
  relationshipOptions,
  methods,
  handleRelationshipChange,
  movingPersonToOtherRecord,
  otherPerson,
  setChangesMade,
  highlightRelationshipFields,
  index,
  setNameFieldIds,
  overlayField,
  temporarySlsPersons = {},
}) => {
  const { t } = useTranslation('RIP')
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const { openOverlay } = useOverlayContext()
  const { thumbnails } = useSharedBrowserStateContext()

  if (typeof index !== 'number') return null
  const path = `relationships[${index}]`
  const formValue = methods.getValues(path)
  if (!formValue) return null // Make sure formValue exists

  let person
  let personIsOnOtherImage
  let pageNumberOfOtherImage
  let arkOfOtherImage
  const personId = formValue.value?.key
  if (personId) {
    person = temporarySlsPersons[personId] || getNestedOrFloatingElementById(state.images, personId)
    personIsOnOtherImage = person?.partitionKey !== state.currentImageArk
    if (personIsOnOtherImage) {
      pageNumberOfOtherImage = (thumbnails?.findIndex((thumbnail) => thumbnail.ark === person?.partitionKey) || 0) + 1
      arkOfOtherImage = person?.partitionKey
    }
  }

  const handleOnFocus = (field) => {
    const activeFieldId = field.subElements ? field?.subElements?.[0]?.id : field.id
    const otherPersonId = field.otherPersonId
    const othrPerson = getNestedOrFloatingElementById(state.images, otherPersonId)
    const foundGiven = getNestedSubelementsByType(othrPerson, 'NAME_GN')
    const foundSurname = getNestedSubelementsByType(othrPerson, 'NAME_SURN')
    const newNameFieldIds = foundGiven.map((x) => x.id).concat(foundSurname.map((x) => x.id))
    setNameFieldIds(newNameFieldIds)
    r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: activeFieldId })
  }

  const isNewlyCreatedPerson = Object.values(r2State.temporaryPersonsDataForRelationships || {}).some(
    (newPersonData) => newPersonData.personId === personId
  )

  return (
    <FieldWrapper indent="0">
      <FormAutosuggest
        data-testid={relationshipField?.relType}
        clearOnClickAway
        clearable
        id={relationshipField.id}
        name={`${path}.value`}
        label={relationshipField.label}
        onFocus={() => handleOnFocus(relationshipField)}
        disabled={movingPersonToOtherRecord && personId !== otherPerson?.id}
        onSuggestionSelected={() => {
          handleRelationshipChange(relationshipField)
          methods.trigger()
          setChangesMade(true)
          highlightRelationshipFields() // Set the highlights when the user selects a new person
        }}
        suggestions={relationshipOptions}
        showLastResortOnFocus
        lastResort={{
          key: 'LAST-RESORT-KEY',
          primaryText: t('family-review.addPerson.name-not-indexed', 'Name Not indexed'),
          onClick: () => {
            r2Dispatch({
              type: 'SET_ADD_PERSON_PANEL_ARGS',
              value: {
                referrer: 'EditRelationshipsPanel',
                recordId: r2State.selectedRecordId,
                metadata: { id: relationshipField.id },
              },
            })
            openOverlay('addPersonPage1Generic')
            overlayField.current = relationshipField
          },
        }}
      />
      {movingPersonToOtherRecord && personId !== otherPerson?.id && (
        <List>
          <ListItem
            dense
            primaryText={t('edit-relationships.relationships-removed', 'Relationship removed on save')}
            startElement={<ListItem.Icon Icon={NoticeWarning} color={colors.red50} />}
          />
        </List>
      )}
      <Row guttersY="xxs">
        {!relationshipField.value && <Separator size="xxs" />}
        {/* Show Edit button only if the person already exists and is not newly created */}
        {relationshipField.value && !isNewlyCreatedPerson && (
          <Button
            Icon={MenuNewWindow}
            onClick={() => {
              window.open(
                `${arkOfOtherImage ?? state.currentImageArk}?view=index&personArk=/ark:/61903/${personId}&action=view`
              )
            }}
          >
            {t('edit-relationships.edit.button', 'Edit')}
          </Button>
        )}
        {relationshipField.value && personIsOnOtherImage && (
          <Button
            Icon={ControlSplit}
            onClick={() =>
              r2Dispatch({
                type: 'OPEN_SPLIT_SCREEN_TO_PAGE',
                value: pageNumberOfOtherImage,
              })
            }
          >
            {t('edit-relationships.view-name.button', 'View on page {pageNumber}', {
              pageNumber: pageNumberOfOtherImage,
            })}
          </Button>
        )}
      </Row>
    </FieldWrapper>
  )
}

export default RelationshipField
